import { observer } from 'mobx-react-lite'

import { type PageBreakScriptInfo } from '#choo-app/lib/editor/plugins/inlinePageBreaks'
import { useFeatureFlag, useMst } from '#hooks'
import { createSnapshotEditorState } from '#util'
import { DEFAULT_READ_RATE } from '#util/constants'
import { getPageLayoutFromScript } from '#util/PageLayout'
import { ScriptSnapshotPayload } from '#util/ScriptoApiClient/types'

import {
  snapshotRevisionsLoader,
  useSnapshotData,
} from '../useSnapshotLandData'
import { SnapshotViewerBase } from '../viewer'

import { BodyPlaceholder } from './BodyPlaceholder'

const PopulatedStaticView = ({
  snapshot,
  scriptName,
}: {
  snapshot: Pick<
    ScriptSnapshotPayload,
    'doc' | 'scriptFormat' | 'scriptId' | 'id' | 'revisions'
  >
  scriptName?: string
}) => {
  const mst = useMst()
  const [legacyPaginator] = useFeatureFlag('legacy-paginator')
  const useNewPaginator = !legacyPaginator
  const { pageless, editorZoom } = mst.user.prefs
  const scriptInfo: PageBreakScriptInfo = {
    name: scriptName ?? '',
    scriptType: snapshot.doc.attrs?.docType,
    readRate: mst.currentOrg?.readRate ?? DEFAULT_READ_RATE,
    scriptPrintPrefs: mst.user.scriptPrintPrefs,
  }
  const editorState = createSnapshotEditorState(
    snapshot,
    mst,
    snapshotRevisionsLoader(mst.scrapi, snapshot),
    useNewPaginator,
    scriptInfo,
  )

  const pageLayout = getPageLayoutFromScript(
    snapshot.doc,
    snapshot.scriptFormat.definition,
  )

  return (
    <SnapshotViewerBase
      pageless={pageless}
      editorZoom={editorZoom}
      editorState={editorState}
      pageLayout={pageLayout}
    />
  )
}

// Once user has supplied an ID
const RequestedStaticView = observer(function StaticView({
  scriptId,
  snapshotId,
  scriptName,
}: {
  scriptId: string
  snapshotId: string
  scriptName?: string
}) {
  const query = useSnapshotData({ scriptId, snapshotId })
  if (query.isPending) {
    return <BodyPlaceholder variant="loading" />
  }
  if (query.isError) {
    return <BodyPlaceholder variant="error" />
  }

  return <PopulatedStaticView snapshot={query.data} scriptName={scriptName} />
})

export const StaticView = observer(function StaticView({
  scriptId,
  scriptName,
}: {
  scriptId: string
  scriptName?: string
}) {
  const { snap1 } = useMst().view.snapshotLand
  if (!snap1) {
    return <BodyPlaceholder variant="pickOne" />
  }

  return (
    <RequestedStaticView
      scriptId={scriptId}
      snapshotId={snap1}
      scriptName={scriptName}
    />
  )
})
